var _excluded = ["id", "buttonText", "children", "className", "isSlideDrawer", "isActionSheet", "isInline", "trigger", "text", "staticModal", "hideTrigger", "onOpen", "onClose", "isActive", "noOverlay", "isFlushMobile", "isPrompt", "hasPortal", "portalContainer", "portalClassName", "size", "onContainerSize", "setOnCloseFocus", "utils"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t["return"] && (u = t["return"](), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React, { useCallback, useRef, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { createFocusTrap } from 'focus-trap';
import { ModalToggleContext } from './context/modal-context';
import { ModalBody, ModalHeader, ModalSubheader, ModalTrigger, ModalFooter } from './components/index';
import Overlay from '../../components/overlay';
import Portal from '../../components/portal';
import Animate from '../animate';
import generateID from '../../utilities/generateID';
import CONSTANTS from '../../appConstants';
import InverseContext from '../../utilities/inverse-context';
import StyledModal from './modal.styles';
import StyledModalContent from './styles/modal.content.styles';
import StyledModalContentContainer from './styles/modal.content-container.styles';
import StyledModalDialog from './styles/modal.dialog.styles';
import { StyledCacheWrapper } from '../../styles';
var ESCAPE_KEY = CONSTANTS.KEY_CODES.ESCAPE;
var modalPropTypes = {
  /** Modal id */
  id: PropTypes.string,
  /** on modal open callback. */
  onOpen: PropTypes.func,
  /** on modal close callback. */
  onClose: PropTypes.func,
  /** Add custom CSS classes to Modal component. */
  className: PropTypes.string,
  /** Add custom trigger to toggle modal.  */
  trigger: PropTypes.func,
  /** Changes the default text inside trigger button. Default: Open Modal. */
  buttonText: PropTypes.string,
  /** Text rendered in custom trigger button */
  text: PropTypes.string,
  /**  Changes the active state of the modal instance. */
  isActive: PropTypes.bool,
  /** If true, clicking on the overlay/pressing escape key will not close modal. Default: false. */
  staticModal: PropTypes.bool,
  /** No trigger button is rendered. Control show/hide of modal using isActive prop and state. */
  hideTrigger: PropTypes.bool,
  /**  Changes the appearance of the modal to be a slide drawer component. */
  isSlideDrawer: PropTypes.bool,
  /**  Changes the appearance of the modal to be an action sheet component. */
  isActionSheet: PropTypes.bool,
  /** Changes the appearance of the modal to have no overlay component. */
  noOverlay: PropTypes.bool,
  /** The top of the modal will be inline with the trigger button. <strong>Note:</strong> only renders in conjunction with <code>isSlideDrawer</code> and <code>noOverlay</code>. */
  isInline: PropTypes.bool,
  /** Child elements to be rendered inside of the modal */
  children: PropTypes.node,
  /** To remove container rounded top corners in mobile view*/
  isFlushMobile: PropTypes.bool,
  /** To Adjust the body and container height */
  isPrompt: PropTypes.bool,
  /** Render the Modal in a react portal */
  hasPortal: PropTypes.bool,
  /** Portal container */
  portalContainer: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Add custom CSS classes to Portal Container. */
  portalClassName: PropTypes.string,
  /** Modal size on desktop view */
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  /** Callback for when component resizes */
  onContainerSize: PropTypes.func,
  /** A ref to the component that should be focused when the modal closes */
  setOnCloseFocus: PropTypes.shape({
    current: PropTypes.shape({
      type: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
      props: PropTypes.object
    })
  }),
  /** Add utility objects for styling */
  utils: PropTypes.object
};
var modalDefaultProps = {
  onOpen: function onOpen() {},
  onClose: function onClose() {},
  buttonText: 'Open Modal',
  text: 'Toggle Modal',
  id: '',
  className: '',
  trigger: null,
  isSlideDrawer: false,
  isActionSheet: true,
  isInline: false,
  staticModal: false,
  isActive: null,
  hideTrigger: false,
  noOverlay: false,
  children: /*#__PURE__*/React.createElement("div", null),
  isFlushMobile: false,
  isPrompt: false,
  hasPortal: false,
  portalContainer: null,
  portalClassName: '',
  size: 'md',
  onContainerSize: function onContainerSize() {},
  setOnCloseFocus: {
    current: undefined
  },
  utils: {}
};
function createOverlay(isActiveState, noOverlay, isActionSheet, isPrompt, isSlideDrawer, onClickOverlay, staticModal) {
  if (noOverlay && isSlideDrawer || noOverlay && isActionSheet) {
    return;
  }
  return /*#__PURE__*/React.createElement(Animate, {
    isActive: isActiveState,
    variant: "fade"
  }, /*#__PURE__*/React.createElement(Overlay, {
    isHidden: false,
    role: "presentation",
    onClick: staticModal || isPrompt ? null : onClickOverlay
  }));
}
function toggleFocusTrap(focusTrap, isInline, isOpen) {
  if (!isInline && isOpen && focusTrap) {
    focusTrap.activate();
  }
  if (!isInline && !isOpen && focusTrap) {
    focusTrap.deactivate();
  }
}
function toggle(isActive, open, close, event) {
  return isActive ? close(event) : open();
}

/**
 * @ignore @description
 * ## Modal Component
 * The purpose of a modal window is to completely take the user’s focus and attention off page and into the new window. This is a beneficial user constraint because we can focus the users attention on completing a task.
 *
 * @ignore @see Mesh: [Modal Docs](https://mesh-component-library.apps.csts-k8cnp-ro-1.r-caas.allstate.com/?path=/docs/containers-modal--default)
 *
 * @ignore @example
 * ```jsx
 * import { Modal } from 'mesh-component-library';
 *
 * <Modal buttonText="Open Modal">
 *   <Modal.Header subTitle="More info" title="Standard Modal" />
 *   <Modal.Subheader>Subheader</Modal.Subheader>
 *   <Modal.Body><p>body text</p></Modal.Body>
 *   <Modal.Footer>
 *       <Button shape="pill" isMuted>
 *         Cancel
 *      </Button>
 *       <Button shape="pill" isPrimary utils={{ hrLeft: 'md' }}>
 *         Submit
 *       </Button>
 *    </Modal.Footer>
 * </Modal>
 * ```
 */

var Modal = function Modal(props) {
  var id = props.id,
    buttonText = props.buttonText,
    children = props.children,
    className = props.className,
    isSlideDrawer = props.isSlideDrawer,
    isActionSheet = props.isActionSheet,
    isInline = props.isInline,
    trigger = props.trigger,
    text = props.text,
    staticModal = props.staticModal,
    hideTrigger = props.hideTrigger,
    onOpen = props.onOpen,
    onClose = props.onClose,
    isActive = props.isActive,
    noOverlay = props.noOverlay,
    isFlushMobile = props.isFlushMobile,
    isPrompt = props.isPrompt,
    hasPortal = props.hasPortal,
    portalContainer = props.portalContainer,
    portalClassName = props.portalClassName,
    size = props.size,
    onContainerSize = props.onContainerSize,
    setOnCloseFocus = props.setOnCloseFocus,
    utils = props.utils,
    other = _objectWithoutProperties(props, _excluded);
  var useId = id || generateID('modal-');
  var focusTrap = useRef(null);

  /**
   * 0 - exited / unmounted
   * 1 - entering
   * 2 - entered
   * 3 - exiting
   */
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    activeState = _useState2[0],
    setActiveState = _useState2[1];
  var onRefSet = useCallback(function (node) {
    if (node) {
      focusTrap.current = createFocusTrap(node, {
        escapeDeactivates: false,
        fallbackFocus: node,
        setReturnFocus: setOnCloseFocus.current,
        preventScroll: true
      });
    }
  }, []);
  var Trigger = trigger ? trigger : ModalTrigger;
  var renderTrigger = /*#__PURE__*/React.createElement(Trigger, {
    buttonText: buttonText,
    onClick: function onClick() {
      return onClickTrigger();
    },
    text: text
  });
  var open = function open() {
    setActiveState(1);
    onOpen(true);
  };
  var close = function close(event) {
    // controlled pattern - first click or first time firing this is the INTENTION of closing
    if (isActive === null) {
      setActiveState(3);
      onClose(event);
    }
    if (isActive) {
      onClose(event);
    }
  };
  var onClickOverlay = function onClickOverlay(event) {
    return close(event);
  };
  var onClickTrigger = function onClickTrigger(event) {
    toggle(activeState, open, close, event);
  };
  var onKeydown = function onKeydown(event) {
    var key = event.key;
    if (key === 'Escape' || key === ESCAPE_KEY) {
      close(event);
    }
  };
  var onAnimateClose = function onAnimateClose() {
    setActiveState(0);
  };
  useEffect(function () {
    return function () {
      if (focusTrap.current) {
        focusTrap.current.deactivate();
      }
    };
  }, []);
  useEffect(function () {
    if (activeState === 1) {
      setActiveState(2);
    } else if (activeState > 1) {
      toggleFocusTrap(focusTrap.current, isInline, activeState === 2);
    }
  }, [activeState]);
  useEffect(function () {
    if (isActive === true) {
      open();
    } else if (isActive === false) {
      // if open, start closing animation
      if (activeState === 2) {
        setActiveState(3);
        onClose(true);
      } else {
        setActiveState(0);
      }
    }
  }, [isActive]);
  var contextValue = useMemo(function () {
    return {
      onClickTrigger: onClickTrigger,
      titleId: useId + '-title',
      bodyId: useId + '-body',
      closeId: useId + '-close'
    };
  }, [onClickTrigger, useId]);
  return /*#__PURE__*/React.createElement(StyledCacheWrapper, null, /*#__PURE__*/React.createElement(ModalToggleContext.Provider, {
    value: contextValue
  }, hideTrigger ? null : renderTrigger, activeState > 0 && /*#__PURE__*/React.createElement(Portal, {
    containerClass: "c-portal-modal ".concat(portalClassName),
    container: portalContainer,
    hasPortal: hasPortal
  }, /*#__PURE__*/React.createElement(StyledModal, _extends({}, other, {
    id: useId,
    className: className,
    size: size,
    tabIndex: -1,
    ref: onRefSet,
    onKeyDown: staticModal ? null : onKeydown
  }), createOverlay(activeState === 2, noOverlay, isActionSheet, isPrompt, isSlideDrawer, onClickOverlay, staticModal), /*#__PURE__*/React.createElement(StyledModalDialog, {
    className: className,
    role: "dialog",
    "aria-labelledby": useId + '-title',
    "aria-describedby": useId + '-body'
  }, /*#__PURE__*/React.createElement(Animate, {
    isActive: activeState === 2,
    onClose: onAnimateClose,
    onContainerSize: onContainerSize,
    variant: isSlideDrawer ? 'slideRight' : 'slideBottom'
  }, /*#__PURE__*/React.createElement(StyledModalContent, {
    className: className,
    isSlideDrawer: isSlideDrawer,
    isPrompt: isPrompt,
    isActionSheet: isActionSheet,
    isFlushMobile: isFlushMobile,
    role: "document",
    utils: utils
  }, /*#__PURE__*/React.createElement(StyledModalContentContainer, {
    isSlideDrawer: isSlideDrawer
  }, /*#__PURE__*/React.createElement(InverseContext.Provider, {
    value: false
  }, children)))))))));
};
Modal.propTypes = modalPropTypes;
Modal.defaultProps = modalDefaultProps;

/**
 * ## Modal Header
 * @see Mesh: [Modal Docs](https://mesh-component-library.apps.csts-k8cnp-ro-1.r-caas.allstate.com/?path=/story/containers-modal-subcomponents-modal-header--default)
 * @example
 * ```jsx
 * import { Modal } from 'mesh-component-library';
 *
 * <Modal.Header {...props}>{children}</Modal.Header>
 * ```
 */
Modal.Header = ModalHeader;

/**
 * ## Modal Subheader
 * @see Mesh: [Modal Docs](https://mesh-component-library.apps.csts-k8cnp-ro-1.r-caas.allstate.com/?path=/story/containers-modal-subcomponents-modal-subheader--default)
 * @example
 * ```jsx
 * import { Modal } from 'mesh-component-library';
 *
 * <Modal.Subheader {...props}>{children}</Modal.Subheader>
 * ```
 */
Modal.Subheader = ModalSubheader;

/**
 * ## Modal Footer
 * @see Mesh: [Modal Docs](https://mesh-component-library.apps.csts-k8cnp-ro-1.r-caas.allstate.com/?path=/story/containers-modal-subcomponents-modal-footer--default)
 * @example
 * ```jsx
 * import { Modal } from 'mesh-component-library';
 *
 * <Modal.Footer {...props}>{children}</Modal.Footer>
 * ```
 */
Modal.Footer = ModalFooter;

/**
 * ## Modal Body
 * @see Mesh: [Modal Docs](https://mesh-component-library.apps.csts-k8cnp-ro-1.r-caas.allstate.com/?path=/story/containers-modal-subcomponents-modal-body--default)
 * @example
 * ```jsx
 * import { Modal } from 'mesh-component-library';
 *
 * <Modal.Body {...props}>{children}</Modal.Body>
 * ```
 */
Modal.Body = ModalBody;
export default Modal;